import { useState } from "react";

import { useLanguage } from "../i18n";

import type { Specimen } from "../types";

const CONDITIONS = [
  { value: "healthy", key: "healthy" },
  { value: "stressed", key: "stressed" },
  { value: "recovering", key: "recovering" },
] as const;

const SOIL = [
  { value: "good", key: "drainsFreely" },
  { value: "compacted", key: "compacted" },
] as const;

/**
 * Both of these change what the engine does — Condition gates feeding, Soil
 * Quality lengthens the Check Interval — so they are fields the app reads, not
 * notes it cannot see.
 */
export function PlantState({ plant, onChanged }: { plant: Specimen; onChanged: () => void }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function update(patch: { condition?: string; soil_quality?: string }) {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`/v1/specimens/${plant.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!response.ok) throw new Error(`The API answered ${response.status}.`);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="state-editor">
      <h2>{t.howItIsDoing}</h2>

      <div className="state-row">
        <span className="state-label">{t.condition}</span>
        <div className="button-row">
          {CONDITIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="action"
              aria-pressed={plant.condition === option.value}
              disabled={busy}
              onClick={() => update({ condition: option.value })}
            >
              {t[option.key]}
            </button>
          ))}
        </div>
      </div>

      <div className="state-row">
        <span className="state-label">{t.soil}</span>
        <div className="button-row">
          {SOIL.map((option) => (
            <button
              key={option.value}
              type="button"
              className="action"
              aria-pressed={plant.soil_quality === option.value}
              disabled={busy}
              onClick={() => update({ soil_quality: option.value })}
            >
              {t[option.key]}
            </button>
          ))}
        </div>
      </div>

      <p className="state-note">
        {plant.condition !== "healthy" && `${t.stateNoteNotFed} `}
        {plant.soil_quality === "compacted" && `${t.stateNoteCompacted} `}
        {t.stateNoteRepot}
      </p>

      {error && <p className="logger-error">{error}</p>}
    </section>
  );
}
